<template>
  <v-row>
    <v-col 
      v-if="editionMode"
      md="3"
    >
      <v-card
        class="pa-5 ml-2"
      >
        <v-toolbar color="grey lighten-3" flat dense text>
          <v-toolbar-title>{{ $t('campaigns.label.newSessions')}}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <v-row>
          <v-col>
            <v-switch
              v-model="allSessions"
              :label="$t('campaigns.label.allSessions')"
            ></v-switch>
          </v-col>
        </v-row>
        <v-row v-if="!allSessions">
          <v-col>
            <v-select
              dense
              v-model="newSessionIds"
              :items="campaign.sessions"
              item-text="label"
              item-value="id"
              multiple
              chips
              :hint="$t('campaigns.label.sessions')"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-switch
              v-model="allSessionModes"
              :label="$t('campaigns.label.allSessionModes')"
            ></v-switch>
          </v-col>
        </v-row>
        <v-row v-if="!allSessionModes">
          <v-col>
            <v-select
              dense
              v-model="newSessionModeIds"
              :items="campaign.sessionModes"
              item-text="label"
              item-value="id"
              multiple
              chips
              :hint="$t('campaigns.label.sessionModes')"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-switch
              v-model="allUsers"
              :label="$t('campaigns.label.allUsers')"
            ></v-switch>
          </v-col>
        </v-row>
        <v-row v-if="!allUsers">
          <v-col>
            <v-subheader class="pl-0">
              {{ $t('campaigns.label.userGroups') }}
            </v-subheader>
            <v-select
              dense
              v-model="newGroupLabels"
              :items="campaign.userGroups"
              item-text="label"
              item-value="label"
              multiple
              chips
              :hint="$t('campaigns.label.userGroups')"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="!allUsers">
          <v-col>
            <v-subheader class="pl-0">
              {{ $t('campaigns.label.users') }}
            </v-subheader>
            <v-select
              dense
              v-model="newUsernames"
              :items="usersOfAllGroups"
              item-text="profilename"
              item-value="username"
              multiple
              chips
              :hint="$t('campaigns.label.users')"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :disabled="!allSessions && (!newSessionIds || newSessionIds.length == 0) || !allSessionModes && (!newSessionModeIds || newSessionModeIds.length == 0) || !allUsers && ((!newUsernames || newUsernames.length == 0) && (!newGroupLabels || newGroupLabels.length == 0))"
              id="create_user_sessions"
              @click="createSessions"
            >
              {{ $t('campaigns.label.createSessions') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col :class="editionMode ? 'md-9' : 'md-12'">
      <v-card class="mx-2">
        <v-card-title>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('campaigns.label.search')"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :items-per-page="-1"
          :headers="headers"
          :items="richUserSessions"
          :search="search"
          show-group-by
        >
          <template v-slot:item.actions="{ item }">
            <div >
              <v-icon
                v-if="editionMode"
                small
                @click="deleteItem(item)"
              >
                {{ item.existing ? 'mdi-delete' : 'mdi-close' }}
              </v-icon>
              <TooltipIconButton
                v-else-if="item.state == 'NEW' || item.state == 'CLOSED'"
                icon="mdi-lock-open-variant-outline"
                id="open_session"
                :tooltip="$t('campaigns.label.openSession')"
                @click="onAction(item, 'open')"
              ></TooltipIconButton>
              <TooltipIconButton
                v-else
                icon="mdi-lock-outline"
                id="close_session"
                :tooltip="$t('campaigns.label.closeSession')"
                @click="onAction(item, 'close')"
              ></TooltipIconButton>
              <TooltipIconButton
                v-if="!editionMode && item.existing && !item.exporting"
                icon="mdi-cloud-download"
                id="export_session"
                :tooltip="$t('campaigns.label.exportUserSession')"
                @click="exportUserSessionWithLoading(item)"
              ></TooltipIconButton>
              <v-progress-circular v-else-if="item.exporting" indeterminate small color="primary">
              </v-progress-circular>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>

import TooltipIconButton from "../utils/TooltipIconButton.vue"
import { v4 as uuidv4 } from 'uuid';

export default {
  props: [ "users", "campaign", "editionMode"],
  components: {
    TooltipIconButton,
  },
  data() {
    return {
      jsonpath: "$.userSessions",
      allUsers: true,
      newUsernames: [],
      newGroupLabels: [],
      allSessions: true,
      newSessionIds: [],
      allSessionModes: true,
      newSessionModeIds: [],
      search: "",
    }
  },
  watch: {
    editionMode: function(value) {
      if (value) {
        this.allUsers = true
        this.allSessionModes = true
        this.allSessions = true
        this.newUsernames.splice(0, this.newUsernames.length)
        this.newGroupLabels.splice(0, this.newGroupLabels.length)
        this.newSessionIds.splice(0, this.newSessionIds.length)
        this.newSessionModeIds.splice(0, this.newSessionModeIds.length)
      }
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('campaigns.label.users'),
          align: 'start',
          value: 'profilename',
        },
        { text: this.$t('campaigns.label.session'), value: 'sessionLabel' },
        { text: this.$t('campaigns.label.sessionMode'), value: 'sessionModeLabel' },
        { text: this.$t('campaigns.label.stateLabel'), value: 'stateLabel' },
        { text: this.$t('campaigns.label.actions'), value: "actions", groupable: false, sortable: false },
      ]

    },
    richUserSessions() {
      const result = []
      for (let userSession of this.campaign.userSessions) {
        const user = this.users.find(user => user.username == userSession.username)
        const hasSessions = this.campaign && this.campaign.sessions
        const session = hasSessions ? this.campaign.sessions.find(session => session.id == userSession.sessionId) : ""
        const hasSessionModes = this.campaign && this.campaign.sessionModes
        const sessionMode = hasSessionModes ? this.campaign.sessionModes.find(sessionMode => sessionMode.id == userSession.sessionModeId) : ""
        result.push({
          ...userSession,
          sessionLabel: session ? session.label : "",
          sessionModeLabel: sessionMode ? sessionMode.label : "",
          profilename: user ? user.profilename : "",
          state: userSession.state,
          stateLabel: this.$t(`campaigns.label.state.${userSession.state}`
          )
        })
      }
      return result
    },
    usersOfAllGroups() {
      const result = []
      const usernamesSet = {}
      for (let userGroup of this.campaign.userGroups) {
        for (let username of userGroup.users) {
          if (!usernamesSet[username]) {
            usernamesSet[username] = true
            result.push(this.users.find(user => user.username == username))
          }
        }
      }
      return result
    },
  }, 
  methods: {
    createSessions() {
      const sessionIds = this.allSessions ? this.campaign.sessions.map(session => session.id) : this.newSessionIds
      const sessionModeIds = this.allSessionModes ? this.campaign.sessionModes.map(sessionMode => sessionMode.id) : this.newSessionModeIds
      const usernames = this.allUsers ? this.usersOfAllGroups.map(user => user.username) : [...this.newUsernames]
      for (let newGroupLabel of this.newGroupLabels) {
        const userGroup = this.campaign.userGroups.find(userGroup => userGroup.label == newGroupLabel)
        if (userGroup) {
          for (let username of userGroup.users) {
            if (!usernames.includes(username))
            usernames.push(username)
          }
        }
      }
      for (let username of usernames) {
        for (let sessionModeId of sessionModeIds) {
          for (let sessionId of sessionIds) {
            // make sure there is no such combination that is not closed
            if (!this.campaign.userSessions.find(userSession => userSession.username == username && userSession.sessionId == sessionId && userSession.sessionModeId == sessionModeId && userSession.state != "CLOSED")) {
              this.$emit("change", { jsonpath: this.jsonpath, op: { name: "add", value: {
                uuid: uuidv4(),
                username,
                sessionId,
                sessionModeId, 
                state: "JUST_CREATED",
                exporting: false,
              }}})
            }
          }
        }
      }
    },
    deleteItem(deletedUserSession) {
      const index = this.campaign.userSessions.findIndex(userSession => userSession.uuid == deletedUserSession.uuid)
      this.$emit("change", { jsonpath: this.jsonpath, op: { name: "remove", value : index }})
    },
    onAction(userSession, action) {
      this.$emit("action", {name: "_user_session_actions", value: [{ campaignId: this.campaign.id, userSessionId: userSession.id, action }]})
    },
    async exportUserSessionWithLoading(userSession) {
      userSession.exporting = true
      try {
        await this.$store._exportUserSession({ 
          campaignId: this.campaign.id, 
          userSessionId: userSession.id, 
          username: userSession.username, 
          sessionLabel: userSession.sessionLabel 
          })
      } finally {
        userSession.exporting = false
      }
    }
  }
}
</script>